import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import QuillRichText from 'components/QuillRichText';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TFaqMgmt } from '../../../type';

interface MedicationModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TFaqMgmt) => void;
    initialData?: TFaqMgmt;
    faqName?: string;
}

const FAQModal: React.FC<MedicationModalProps> = ({
    visible,
    onCancel,
    onSave,
    initialData,
    faqName = 'FAQ',
}) => {
    const [form] = Form.useForm<TFaqMgmt>();
    const [charCounts, setCharCounts] = useState({
        question: 0,
        answer: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TFaqMgmt>) => {
        setCharCounts({
            question: (data.question || '').length,
            answer: (data.answer || '').replace(/<[^>]*>/g, '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                // 编辑模式：设置所有字段包括priority（虽然不显示）
                form.setFieldsValue({
                    question: initialData.question,
                    answer: initialData.answer,
                    priority: initialData.priority, // 保持priority字段
                });
                updateCharCounts(initialData);
            } else {
                // 新增模式：设置默认值
                const defaultValues = {
                    priority: 1,
                    question: '',
                    answer: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    question: 0,
                    answer: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TFaqMgmt, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'question') {
            newCounts.question = value.length;
        } else if (fieldName === 'answer') {
            newCounts.answer = value.replace(/<[^>]*>/g, '').length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={initialData ? `Edit ${faqName}` : `Add ${faqName}`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* Hidden Priority Field - 保持priority值 */}
                <Form.Item name="priority" hidden>
                    <Input type="hidden" />
                </Form.Item>

                {/* Name Field */}
                <Form.Item
                    label="Question"
                    name="question"
                    rules={[
                        { required: true, message: 'Please enter question' },
                        { max: 100, message: 'Question cannot exceed 100 characters' },
                    ]}
                >
                    <Input
                        className={s.nameInput}
                        placeholder="What is ADHD?"
                        maxLength={100}
                        onChange={(e) => handleFieldChange('question', e.target.value)}
                    />
                </Form.Item>

                {/* Details Field */}
                <Form.Item
                    label="Answer"
                    name="answer"
                    rules={[
                        { required: true, message: 'Please enter answer' },
                        {
                            validator: (_, value) => {
                                const plainText = value?.replace(/<[^>]*>/g, '') || '';
                                if (plainText.length > 1000) {
                                    return Promise.reject(new Error('Answer cannot exceed 1000 characters'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <QuillRichText
                        keepHtmlLabels
                        height="auto"
                        value={form.getFieldValue('answer')}
                        // plcaeholder="Used for patients with Attention Deficit Hyperactivity Disorder (ADHD). This medication helps improve focus and reduce impulsivity by affecting certain natural substances in the brain."
                        onChange={(value) => handleFieldChange('answer', value)}
                        showCount
                        maxLength={1000}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FAQModal;
